import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
} from "@tanstack/react-table";
import { ArrowDown, ArrowUp, Bell, ChevronsUpDown } from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";

import { Badge } from "../../../ui/badge";
import { Button } from "../../../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../ui/dialog";
import { Label } from "../../../ui/label";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "../../../ui/pagination";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../../../ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../ui/table";
import { Textarea } from "../../../ui/textarea";
import { formatLogDate, useReconciliationActions } from "../lib/api/exceptions-log";
import type { ExceptionLogRow, RuleDefinition, RuleId, RuleTier } from "../lib/api/types";
import { cn } from "../lib/utils";
import { RULE_TIER_COLORS } from "./PolicyComplianceCharts";

function tierColor(tier: RuleTier) {
  if (tier === "DQ") return "var(--neutral-inactive)";
  return RULE_TIER_COLORS[tier];
}

const currency = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

function RuleBadge({ ruleId, tier }: { ruleId: RuleId; tier: RuleTier }) {
  const color = tierColor(tier);
  return (
    <Badge
      variant="outline"
      className="gap-1.5 px-2 py-0.5 text-[11px] tabular-nums"
      style={{
        borderColor: `color-mix(in oklch, ${color} 55%, transparent)`,
        background: `color-mix(in oklch, ${color} 16%, transparent)`,
        color,
      }}
    >
      {ruleId}
      <span className="font-normal opacity-80">Tier {tier}</span>
    </Badge>
  );
}

function ReconciliationBadge({ row, tier }: { row: ExceptionLogRow; tier: RuleTier }) {
  const confirmed = row.reconciliationStatus === "confirmed";
  const inAlertScope = tier === "A" && !confirmed;

  return (
    <span className="inline-flex items-center gap-1.5">
      <Badge
        variant="outline"
        className={cn(
          confirmed
            ? "border-primary/50 bg-amber-dim text-primary"
            : "border-neutral-inactive/40 bg-neutral-inactive-dim text-neutral-inactive",
        )}
      >
        {confirmed ? "Confirmed" : "Candidate"}
      </Badge>
      {inAlertScope ? (
        <Bell
          className="size-3.5 text-primary"
          aria-label="Within scope of the alert system — thresholds are evaluated in Manage Alerts"
        />
      ) : null}
    </span>
  );
}

interface TableMeta {
  tierOf: (ruleId: RuleId) => RuleTier;
  labelOf: (ruleId: RuleId) => string;
  onReconcile: (row: ExceptionLogRow) => void;
}

const columnHelper = createColumnHelper<ExceptionLogRow>();

function makeColumns(meta: TableMeta): ColumnDef<ExceptionLogRow, any>[] {
  return [
    columnHelper.accessor("applicationId", {
      header: "Application ID",
      cell: (info) => (
        <span className="font-medium tabular-nums text-foreground">{info.getValue()}</span>
      ),
    }),
    columnHelper.accessor("ruleId", {
      header: "Rule",
      cell: (info) => (
        <span className="inline-flex flex-col gap-1">
          <RuleBadge ruleId={info.getValue()} tier={meta.tierOf(info.getValue())} />
          <span className="text-[11px] text-muted-foreground">{meta.labelOf(info.getValue())}</span>
        </span>
      ),
    }),
    columnHelper.accessor("riskTier", {
      header: "Risk Tier / PG",
      cell: ({ row }) => (
        // pg is null where Athena never scored the application — neither PG nor Corp.
        // A plain truthiness check would have labelled those "Corp", inventing a
        // guarantor structure the data does not record.
        <span className="tabular-nums text-foreground">
          {row.original.riskTier} ·{" "}
          <span
            className={row.original.pg === true ? "text-foreground" : "text-muted-foreground"}
          >
            {row.original.pg === true ? "PG" : row.original.pg === false ? "Corp" : "Not scored"}
          </span>
        </span>
      ),
    }),
    columnHelper.accessor("submitAmount", {
      header: "Ticket Size",
      cell: (info) => (
        <span className="tabular-nums text-foreground">{currency.format(info.getValue())}</span>
      ),
    }),
    columnHelper.accessor("analystUserId", {
      header: "Analyst",
      cell: (info) => <span className="tabular-nums text-muted-foreground">{info.getValue()}</span>,
    }),
    columnHelper.accessor("timestamp", {
      header: "Timestamp",
      cell: (info) => (
        <span className="whitespace-nowrap tabular-nums text-muted-foreground">
          {formatLogDate(info.getValue())}
        </span>
      ),
    }),
    columnHelper.accessor("reconciliationStatus", {
      header: "Reconciliation",
      cell: ({ row }) => (
        <ReconciliationBadge row={row.original} tier={meta.tierOf(row.original.ruleId)} />
      ),
    }),
    columnHelper.accessor("daysUnreconciled", {
      header: "Days Unreconciled",
      cell: (info) => {
        const value = info.getValue();
        return (
          <span className="tabular-nums text-foreground">
            {value == null ? <span className="text-neutral-inactive">—</span> : value}
          </span>
        );
      },
    }),
    columnHelper.display({
      id: "actions",
      header: "Actions",
      cell: ({ row }) =>
        row.original.reconciliationStatus === "candidate" ? (
          <Button
            size="sm"
            variant="outline"
            className="h-7 border-primary/50 text-xs text-primary hover:bg-amber-dim"
            onClick={(event) => {
              event.stopPropagation();
              meta.onReconcile(row.original);
            }}
          >
            Mark reconciled
          </Button>
        ) : null,
    }),
  ];
}

function DetailField({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="space-y-1">
      <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <div className="text-sm text-foreground">{value}</div>
    </div>
  );
}

export function ExceptionsLogTable({
  rows,
  ruleDefinitions,
}: {
  rows: ExceptionLogRow[];
  ruleDefinitions: RuleDefinition[];
}) {
  const [sorting, setSorting] = useState([{ id: "timestamp", desc: true }]);
  const [detailRow, setDetailRow] = useState<ExceptionLogRow | null>(null);
  const [reconcileRow, setReconcileRow] = useState<ExceptionLogRow | null>(null);
  const [note, setNote] = useState("");
  const { markReconciled, isPending } = useReconciliationActions();

  const lookup = useMemo(() => {
    const map = new Map<RuleId, RuleDefinition>();
    for (const def of ruleDefinitions) map.set(def.ruleId, def);
    return map;
  }, [ruleDefinitions]);

  const columns = useMemo(
    () =>
      makeColumns({
        tierOf: (ruleId) => lookup.get(ruleId)?.ruleTier ?? "B",
        labelOf: (ruleId) => lookup.get(ruleId)?.label ?? ruleId,
        onReconcile: (row) => {
          setNote("");
          setReconcileRow(row);
        },
      }),
    [lookup],
  );

  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageIndex: 0, pageSize: 20 } },
  });

  const submit = async () => {
    if (!reconcileRow || !note.trim()) return;
    await markReconciled({
      applicationId: reconcileRow.applicationId,
      reconciledNote: note.trim(),
    });
    setReconcileRow(null);
    setDetailRow(null);
    setNote("");
  };

  const pageCount = table.getPageCount();
  const pageIndex = table.getState().pagination.pageIndex;
  const detailTier = detailRow ? (lookup.get(detailRow.ruleId)?.ruleTier ?? "B") : "B";

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto rounded-lg border border-border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((group) => (
              <TableRow key={group.id}>
                {group.headers.map((header) => {
                  const sortable = header.column.getCanSort();
                  const dir = header.column.getIsSorted();
                  const Icon = dir === "asc" ? ArrowUp : dir === "desc" ? ArrowDown : ChevronsUpDown;
                  return (
                    <TableHead key={header.id} className="whitespace-nowrap">
                      {sortable ? (
                        <button
                          type="button"
                          onClick={header.column.getToggleSortingHandler()}
                          className={cn(
                            "inline-flex items-center gap-1.5 text-left transition-colors hover:text-foreground",
                            dir ? "text-foreground" : "",
                          )}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          <Icon className={cn("size-3", dir ? "text-primary" : "opacity-50")} />
                        </button>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="py-10 text-center text-sm text-muted-foreground"
                >
                  No flagged deals match these filters.
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  onClick={() => setDetailRow(row.original)}
                  className="cursor-pointer"
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="whitespace-nowrap py-2.5 text-sm">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs tabular-nums text-muted-foreground">
          {rows.length} flagged deals · page {pageCount === 0 ? 0 : pageIndex + 1} of {pageCount}
        </p>
        <Pagination className="mx-0 w-auto justify-end">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                href="#"
                aria-disabled={!table.getCanPreviousPage()}
                className={cn(
                  "cursor-pointer",
                  !table.getCanPreviousPage() && "pointer-events-none opacity-40",
                )}
                onClick={(event) => {
                  event.preventDefault();
                  table.previousPage();
                }}
              />
            </PaginationItem>
            <PaginationItem>
              <PaginationNext
                href="#"
                aria-disabled={!table.getCanNextPage()}
                className={cn(
                  "cursor-pointer",
                  !table.getCanNextPage() && "pointer-events-none opacity-40",
                )}
                onClick={(event) => {
                  event.preventDefault();
                  table.nextPage();
                }}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>

      {/* Row detail drawer */}
      <Sheet open={detailRow != null} onOpenChange={(open) => !open && setDetailRow(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-md">
          {detailRow ? (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2">
                  {detailRow.applicationId}
                  <RuleBadge ruleId={detailRow.ruleId} tier={detailTier} />
                </SheetTitle>
                <SheetDescription>
                  {lookup.get(detailRow.ruleId)?.label ?? detailRow.ruleId} · {detailRow.officeName}
                </SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <DetailField label="Field changed" value={detailRow.fieldChanged} />
                  <DetailField
                    label="Value that triggered flag"
                    value={
                      <span className="text-decline">{detailRow.valueThatTriggeredFlag}</span>
                    }
                  />
                  <DetailField
                    label="Policy threshold expected"
                    value={detailRow.policyThresholdExpected}
                  />
                  <DetailField
                    label="Ticket size"
                    value={currency.format(detailRow.submitAmount)}
                  />
                  <DetailField
                    label="Risk tier / PG"
                    value={`${detailRow.riskTier} · ${
                      detailRow.pg === true
                        ? "PG"
                        : detailRow.pg === false
                          ? "Corp"
                          : "Not scored"
                    }`}
                  />
                  <DetailField label="Analyst" value={detailRow.analystUserId} />
                  <DetailField label="Timestamp" value={formatLogDate(detailRow.timestamp)} />
                  <DetailField
                    label="Days unreconciled"
                    value={detailRow.daysUnreconciled ?? "—"}
                  />
                  <DetailField
                    label="Reconciliation"
                    value={<ReconciliationBadge row={detailRow} tier={detailTier} />}
                  />
                  {detailRow.reconciledAt ? (
                    <DetailField
                      label="Reconciled at"
                      value={formatLogDate(detailRow.reconciledAt)}
                    />
                  ) : null}
                </div>

                {detailRow.reconciledNote ? (
                  <DetailField
                    label="Reconciliation note"
                    value={
                      <span className="leading-relaxed text-muted-foreground">
                        {detailRow.reconciledNote}
                      </span>
                    }
                  />
                ) : null}

                {detailRow.reconciliationStatus === "candidate" ? (
                  <Button
                    variant="outline"
                    className="border-primary/50 text-primary hover:bg-amber-dim"
                    onClick={() => {
                      setNote("");
                      setReconcileRow(detailRow);
                    }}
                  >
                    Mark reconciled
                  </Button>
                ) : null}
              </div>
            </>
          ) : null}
        </SheetContent>
      </Sheet>

      {/* Reconciliation dialog — direct edit, no approval step */}
      <Dialog open={reconcileRow != null} onOpenChange={(open) => !open && setReconcileRow(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Mark reconciled</DialogTitle>
            <DialogDescription>
              {reconcileRow
                ? `${reconcileRow.applicationId} · ${reconcileRow.ruleId} — this takes effect immediately, with no review step.`
                : null}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="reconciled-note">Reconciliation note</Label>
            <Textarea
              id="reconciled-note"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="What was found in LeaseWorks, and why this is now confirmed."
              rows={4}
              required
            />
            {note.trim() ? null : (
              <p className="text-xs text-muted-foreground">A note is required to confirm.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReconcileRow(null)}>
              Cancel
            </Button>
            <Button onClick={() => void submit()} disabled={!note.trim() || isPending}>
              {isPending ? "Saving…" : "Submit"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
